import { ReactElement, useMemo, useState } from 'react';

import styled from 'styled-components';

import { Badge } from '@components/badge';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { CopyButton } from '@components/copy-button';
import { EmptyState } from '@components/empty-state';
import { createForm, useCreateFormStore } from '@components/form';
import { useDeleteModal } from '@components/generic-modals';
import { HideContainer } from '@components/hide-container';
import { IconButton } from '@components/icon-button';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconAiSparkle } from '@components/icons/ai-sparkle';
import { IconAttributeJourney } from '@components/icons/attribute-journey';
import { IconAttributeTraveller } from '@components/icons/attribute-traveller';
import { IconCheckmarkSmall } from '@components/icons/checkmark-small';
import { IconCloseSmall } from '@components/icons/close-small';
import { IconEdit } from '@components/icons/edit';
import { IconEmail } from '@components/icons/email';
import { IconCode } from '@components/icons/icon-code';
import { IconInfoSmall } from '@components/icons/info-small';
import { IconMailhook } from '@components/icons/mailhook';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconTextShort } from '@components/icons/text-short';
import { IconTrash } from '@components/icons/trash';
import { InfoBox } from '@components/info-box';
import {
  Conditions,
  processConditionsForSubmission,
  useConditionState,
} from '@components/integrations';
import { Spinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { forceHideSidebar, InfoButton, Sidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { FieldType } from '@components/text-editors/addons/field-mapping';
import { TextField } from '@components/text-field';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { abbreviateDate } from '@utils/date';
import { removeUndefined } from '@utils/objects';

import { useCompanyTravellerAttributeTemplates } from 'api/attributes/hooks';
import { AttributeFieldType } from 'api/attributes/requests';
import {
  useDeleteEmailTrigger,
  useEmailTrigger,
  useEmailTriggerLogs,
  useUpdateEmailTrigger,
} from 'api/email-triggers/hooks';
import {
  AttributeFieldConfig,
  GeneratedFieldInstruction,
  JourneyEmailTrigger,
} from 'api/email-triggers/requests';
import { useJourneyAttributes } from 'api/journeys/hooks';
import { Condition, ConditionType } from 'api/types';
import { getFieldMappingId } from 'ui/actions/helpers';
import { AttributeIcon } from 'ui/authenticated/settings/pages/attributes/common';
import {
  ActionHeader,
  DescriptionFormField,
  useRawVariableGroups,
} from 'ui/journey-builder/node-types/common';
import {
  FormWithUnsavedChangesPrompt,
  UnsavedChangesPrompter,
} from 'ui/journey-builder/unsaved-changes-prompt';
import { isInTemplateRoute } from 'ui/router';

import { TriggerLogListCard } from '../common';
import { useEditFieldModal } from '../modals/add-field-modal';
import { useEmailTriggerListenModal } from '../modals/email-trigger-listen';
import { usePreviewEmailTriggerLog } from '../modals/preview-email-trigger-log';

const ATTRIBUTE_TO_FIELD_TYPE: Record<AttributeFieldType, FieldType> = {
  text: 'text',
  paragraph: 'paragraph',
  date: 'date',
  number: 'number',
  decimal: 'decimal',
  single_select: 'select',
  multi_select: 'multi_select',
  phone_number: 'phone',
  email: 'email',
  file: 'file',
  file_multi: 'file_multi',
  boolean: 'boolean',
  user: 'text',
};

type Payload = {
  description: string;
  isActive: boolean;
  isAutoStart: boolean;
  allowDuplicateTravellers: boolean;
  extractionFields: AttributeFieldConfig[];
  [key: string]: any;
};

type Data = Payload & {
  conditionType: ConditionType;
  conditions: Condition[];
};

const Form = createForm<Data>();

export const useEmailTriggerSidebar = () => {
  const sidebar = useSidebar(EmailTriggerSidebar, {
    variant: 'REGULAR',
  });
  return sidebar;
};

type EmailTriggerProps = {
  triggerShortId: string;
  journeyShortId?: string;
};

export const EmailTriggerSidebar = ({ triggerShortId }: EmailTriggerProps) => {
  const { t } = useScopedTranslate(locale);
  const { data: trigger, isLoading } = useEmailTrigger(triggerShortId);
  const isTemplate = isInTemplateRoute();

  if (!trigger || isLoading) {
    return <Spinner />;
  }

  const tabItems = [
    {
      label: t('details'),
      content: <EmailTriggerContent trigger={trigger} />,
    },
    ...(isTemplate
      ? []
      : [
          {
            label: t('logs'),
            content: <EmailTriggerLogs triggerShortId={triggerShortId} />,
            tabCount: trigger.logsCount,
          },
        ]),
  ];

  return (
    <Sidebar>
      <ActionHeader
        icon={IconMailhook}
        iconColor="elementOrange100"
        title={t('emailTrigger')}
        tabs={tabItems}
      >
        <InfoButton>
          <EmailTriggerInfo />
        </InfoButton>
      </ActionHeader>
    </Sidebar>
  );
};

const EmailTriggerInfo = () => {
  const { t } = useScopedTranslate(locale);
  return (
    <Stack space={4}>
      <Text font="bold16" text={t('emailTrigger')} />
      <Text font="regular14" color="textSecondary" text={t('helpText')} />
    </Stack>
  );
};

type InstructionsMap = Record<string, string>;

// Locked-row keys match the backend's BUILTIN_LABELS (apps/journey_email_triggers/services.py).
// Full Name and Email are always extracted; persisted instructions override the default prompt.
const LOCKED_FULL_NAME_KEY = 'company_traveller_name';
const LOCKED_EMAIL_KEY = 'traveller_email';

const EMPTY_ATTRIBUTES: never[] = [];

const EmailTriggerContent = ({ trigger }: { trigger: JourneyEmailTrigger }) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const formValues = formStore.useFormValues();
  const isDirty = formStore.useIsDirty();
  const updateMutation = useUpdateEmailTrigger(trigger);
  const showListenModal = useEmailTriggerListenModal();
  const showEditFieldModal = useEditFieldModal();

  // Build initial instructions map from extraction_fields
  const initialInstructions = useMemo(() => {
    const map: InstructionsMap = {};
    for (const field of trigger.extractionFields) {
      map[field.attributeShortId] = field.instructions;
    }
    return map;
  }, [trigger.extractionFields]);

  const [fieldInstructions, setFieldInstructions] = useState<InstructionsMap>(initialInstructions);

  const hasFieldsChanged = useMemo(() => {
    const currentKeys = Object.keys(fieldInstructions);
    const initialKeys = Object.keys(initialInstructions);
    if (currentKeys.length !== initialKeys.length) return true;
    return currentKeys.some((key) => {
      return fieldInstructions[key] !== initialInstructions[key];
    });
  }, [fieldInstructions, initialInstructions]);

  const handleFieldInstructionsChange = (shortId: string, instructions: string) => {
    setFieldInstructions((prev) => {
      return { ...prev, [shortId]: instructions };
    });
  };

  const handleFieldRemove = (shortId: string) => {
    setFieldInstructions((prev) => {
      const next = { ...prev };
      delete next[shortId];
      return next;
    });
  };

  const handleCreateAndSaveOutput = (fields: GeneratedFieldInstruction[]) => {
    setFieldInstructions((prev) => {
      const next = { ...prev };
      for (const field of fields) {
        next[field.attributeShortId] = field.instructions;
      }
      return next;
    });
  };

  // Load attributes
  const journeyAttributesQuery = useJourneyAttributes(trigger.journeyId, {
    ignore_inactive: true,
  });
  const travellerAttributesQuery = useCompanyTravellerAttributeTemplates({
    queryData: { ignore_inactive: true },
    journeyShortId: trigger.journeyId,
  });

  const journeyAttributes = journeyAttributesQuery.data ?? EMPTY_ATTRIBUTES;
  const travellerAttributes = travellerAttributesQuery.data ?? EMPTY_ATTRIBUTES;

  const isAttributesLoading =
    journeyAttributesQuery.isLoading || travellerAttributesQuery.isLoading;

  const handleAddAndEditField = (shortId: string, title: string, type: AttributeFieldType) => {
    showEditFieldModal({
      icon: type,
      title,
      shortId,
      onSubmit: handleFieldInstructionsChange,
    });
  };

  const handleEditField = (shortId: string) => {
    if (shortId === LOCKED_FULL_NAME_KEY || shortId === LOCKED_EMAIL_KEY) {
      const isFullName = shortId === LOCKED_FULL_NAME_KEY;
      showEditFieldModal({
        icon: isFullName ? 'text' : 'email',
        title: isFullName ? t('fullName') : t('email'),
        shortId,
        initialInstructions: fieldInstructions[shortId],
        onSubmit: handleFieldInstructionsChange,
      });
      return;
    }

    const attribute =
      travellerAttributes.find((traveller_attribute) => traveller_attribute.shortId === shortId) ??
      journeyAttributes.find((journey_attribute) => journey_attribute.shortId === shortId);

    if (!attribute) {
      return;
    }

    showEditFieldModal({
      icon: attribute.attributeType,
      title: attribute.title,
      shortId,
      initialInstructions: fieldInstructions[shortId],
      onSubmit: handleFieldInstructionsChange,
    });
  };

  const getAvailableAttributes = (section: AddFieldMenuProps['section']) => {
    const attributes = section === 'traveller' ? travellerAttributes : journeyAttributes;
    const existing = new Set(Object.keys(fieldInstructions));
    return attributes.filter((attribute) => {
      return attribute.attributeType !== 'user' && !existing.has(attribute.shortId);
    });
  };

  // Build variable groups for conditions from enabled attributes
  const conditionVariableFields = useMemo(() => {
    const fields: { label: string; field: string; type: FieldType; source: 'payload' }[] = [
      {
        label: t('senderEmail'),
        field: 'sender_email',
        type: 'email' as const,
        source: 'payload' as const,
      },
      {
        label: t('senderName'),
        field: 'sender_name',
        type: 'text' as const,
        source: 'payload' as const,
      },
      {
        label: t('emailSubject'),
        field: 'subject',
        type: 'text' as const,
        source: 'payload' as const,
      },
    ];
    for (const attr of travellerAttributes) {
      if (fieldInstructions[attr.shortId]) {
        fields.push({
          label: attr.title,
          field: attr.shortId,
          type: ATTRIBUTE_TO_FIELD_TYPE[attr.attributeType] ?? 'text',
          source: 'payload' as const,
        });
      }
    }
    for (const attr of journeyAttributes) {
      if (fieldInstructions[attr.shortId]) {
        fields.push({
          label: attr.title,
          field: attr.shortId,
          type: ATTRIBUTE_TO_FIELD_TYPE[attr.attributeType] ?? 'text',
          source: 'payload' as const,
        });
      }
    }
    return fields;
  }, [t, travellerAttributes, journeyAttributes, fieldInstructions]);

  const conditionVariableGroups = useRawVariableGroups({
    actionVariables: [
      {
        id: 'payload',
        label: 'Extracted fields',
        fields: conditionVariableFields,
      },
    ],
    types: [
      'email',
      'text',
      'paragraph',
      'phone',
      'date',
      'boolean',
      'select',
      'multi_select',
      'number',
      'decimal',
    ],
  });

  const fieldMappingId = getFieldMappingId(trigger.journeyId);

  const initialConditions = useMemo(
    () => trigger?.conditions?.conditions ?? [],
    [trigger?.conditions?.conditions]
  );

  const {
    conditions,
    setConditions,
    conditionsChanged,
    handleAddCondition,
    handleRemoveCondition,
  } = useConditionState(initialConditions);

  const buildExtractionFields = (): AttributeFieldConfig[] => {
    return Object.entries(fieldInstructions)
      .filter(([, instructions]) => {
        return Boolean(instructions);
      })
      .map(([shortId, instructions]) => {
        return {
          attributeShortId: shortId,
          instructions,
        };
      });
  };

  // Resolve attribute metadata for added (non-locked) rows
  const addedTravellerAttributes = useMemo(() => {
    return travellerAttributes.filter((attr) => {
      return attr.attributeType !== 'user' && Boolean(fieldInstructions[attr.shortId]);
    });
  }, [travellerAttributes, fieldInstructions]);

  const addedJourneyAttributes = useMemo(() => {
    return journeyAttributes.filter((attr) => {
      return attr.attributeType !== 'user' && Boolean(fieldInstructions[attr.shortId]);
    });
  }, [journeyAttributes, fieldInstructions]);

  const handleSubmit = async (data: Data) => {
    const extractionFields = buildExtractionFields();
    const formConditions = processConditionsForSubmission(conditions, formValues);

    const payload: Partial<Payload> = {
      extractionFields,
      conditions: {
        conditions: formConditions,
        conditionType: data.conditionType,
      },
      ...removeUndefined({
        description: data.description,
        isActive: data.isActive,
        isAutoStart: data.isAutoStart,
        allowDuplicateTravellers: data.allowDuplicateTravellers,
      }),
    };

    await updateMutation.mutateAsync(payload, { onSuccess: forceHideSidebar });
  };

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSubmit}>
      <UnsavedChangesPrompter onSubmit={handleSubmit} />
      <Stack space={6}>
        <HideContainer title={t('triggerDetails')}>
          <Stack space={4}>
            <DescriptionFormField defaultValue={trigger.description} />
            <Stack orientation="horizontal" align="flex-end" space={2}>
              <div style={{ flex: 1 }}>
                <TextField
                  label={t('triggerEmail')}
                  defaultValue={trigger.triggerEmail}
                  isDisabled
                  readOnly
                />
              </div>
              <CopyButton getText={() => trigger.triggerEmail} size="md" iconOnly />
            </Stack>
          </Stack>
        </HideContainer>
        <Sidebar.Separator />

        {isAttributesLoading ? (
          <Spinner />
        ) : (
          <Stack space={4}>
            <InfoBox text={t('instructionsInfo')} />
            <Button
              variant="ai-gradient"
              text={t('testConfigureEmailTrigger')}
              iconLeft={<IconAiSparkle color="aiGradientTeal100" />}
              onClick={() => {
                showListenModal({
                  trigger,
                  onCreateAndSaveOutput: handleCreateAndSaveOutput,
                });
              }}
              isFullWidth
            />

            <HideContainer
              title={t('travellerFields')}
              icon={<IconAttributeTraveller color="buttonIconPositive" />}
            >
              <Stack space={2}>
                <Text font="regular14" color="textSecondary" text={t('defineDataSubheading')} />
                <FieldRow
                  icon={<IconTextShort color="elementPurple50" />}
                  title={t('fullName')}
                  instructions={fieldInstructions[LOCKED_FULL_NAME_KEY] ?? ''}
                  onEdit={() => {
                    handleEditField(LOCKED_FULL_NAME_KEY);
                  }}
                />
                <FieldRow
                  icon={<IconEmail color="elementPurple50" />}
                  title={t('email')}
                  instructions={fieldInstructions[LOCKED_EMAIL_KEY] ?? ''}
                  onEdit={() => {
                    handleEditField(LOCKED_EMAIL_KEY);
                  }}
                />
                {addedTravellerAttributes.map((attr) => {
                  return (
                    <FieldRow
                      key={attr.shortId}
                      icon={<AttributeIcon type={attr.attributeType} color="elementPurple50" />}
                      title={attr.title}
                      instructions={fieldInstructions[attr.shortId] ?? ''}
                      onEdit={() => {
                        handleEditField(attr.shortId);
                      }}
                      onRemove={() => {
                        handleFieldRemove(attr.shortId);
                      }}
                    />
                  );
                })}
                <AddFieldMenu
                  section="traveller"
                  availableAttributes={getAvailableAttributes('traveller')}
                  onSelect={handleAddAndEditField}
                />
              </Stack>
            </HideContainer>

            <Sidebar.Separator />

            <HideContainer
              title={t('journeyFields')}
              icon={<IconAttributeJourney color="buttonIconPositive" />}
            >
              <Stack space={2}>
                <Text font="regular14" color="textSecondary" text={t('defineDataSubheading')} />
                {addedJourneyAttributes.length === 0 ? (
                  <EmptyJourneyFields text={t('noJourneyFields')} />
                ) : (
                  addedJourneyAttributes.map((attr) => {
                    return (
                      <FieldRow
                        key={attr.shortId}
                        icon={<AttributeIcon type={attr.attributeType} color="elementPurple50" />}
                        title={attr.title}
                        instructions={fieldInstructions[attr.shortId] ?? ''}
                        onEdit={() => {
                          handleEditField(attr.shortId);
                        }}
                        onRemove={() => {
                          handleFieldRemove(attr.shortId);
                        }}
                      />
                    );
                  })
                )}
                <AddFieldMenu
                  section="journey"
                  availableAttributes={getAvailableAttributes('journey')}
                  onSelect={handleAddAndEditField}
                />
              </Stack>
            </HideContainer>
          </Stack>
        )}

        <Sidebar.Separator />

        <HideContainer title={t('addConditions')} caps>
          <Conditions
            conditions={conditions}
            conditionType={trigger.conditions?.conditionType}
            variableGroups={conditionVariableGroups}
            fieldMappingId={fieldMappingId}
            onAddCondition={handleAddCondition}
            onRemoveCondition={handleRemoveCondition}
            setConditions={setConditions}
            Form={Form}
          />
        </HideContainer>

        <Sidebar.Separator />

        <HideContainer title={t('options')}>
          <Stack space={4}>
            <Form.Switch
              key={`isActive-${trigger.isActive}`}
              id="isActive"
              label={t('active')}
              defaultValue={trigger.isActive}
            />
            <Form.Switch
              key={`isAutoStart-${trigger.isAutoStart}`}
              id="isAutoStart"
              label={t('startOnSubmission')}
              defaultValue={trigger.isAutoStart}
            />
            <Form.Switch
              key={`allowDuplicateTravellers-${trigger.allowDuplicateTravellers}`}
              id="allowDuplicateTravellers"
              label={t('allowDuplicates')}
              defaultValue={trigger.allowDuplicateTravellers}
            />
          </Stack>
        </HideContainer>
      </Stack>
      <Footer
        trigger={trigger}
        isDisabled={!isDirty && !conditionsChanged && !hasFieldsChanged && trigger.isConfigured}
      />
    </FormWithUnsavedChangesPrompt>
  );
};

type FieldRowProps = {
  icon: ReactElement;
  title: string;
  instructions: string;
  onEdit: () => void;
  onRemove?: () => void;
};

const FieldRow = ({ icon, title, instructions, onEdit, onRemove }: FieldRowProps) => {
  const { t } = useScopedTranslate(locale);
  const tooltipContent = instructions || t('noInstructions');

  return (
    <Card spaceX={3} spaceY={1} interactive onClick={onEdit}>
      <Stack orientation="horizontal" justify="space-between">
        <Stack orientation="horizontal" space={2} align="center" style={{ overflow: 'hidden' }}>
          {icon}
          <Text text={title} ellipsisWithTooltip />
          <IconInfoSmall color="purple50" tooltip={tooltipContent} />
        </Stack>
        <Menu
          buttonProps={{
            variant: 'ghost',
            iconRight: <IconMoreVertical />,
            size: 'sm',
          }}
        >
          <Menu.Item label={t('core.edit')} icon={<IconEdit />} onSelect={onEdit} />
          {onRemove && (
            <Menu.Item
              label={t('core.delete')}
              variant="danger"
              icon={<IconTrash />}
              onSelect={onRemove}
            />
          )}
        </Menu>
      </Stack>
    </Card>
  );
};

type AddFieldMenuProps = {
  section: 'traveller' | 'journey';
  availableAttributes: Array<{
    shortId: string;
    title: string;
    attributeType: AttributeFieldType;
  }>;
  onSelect: (shortId: string, title: string, type: AttributeFieldType) => void;
};

const AddFieldMenu = ({ availableAttributes, onSelect }: AddFieldMenuProps) => {
  const { t } = useScopedTranslate(locale);

  return (
    <Stack orientation="horizontal">
      <Menu
        buttonProps={{
          variant: 'secondary',
          text: t('addField'),
          iconLeft: <IconAddPlus />,
        }}
      >
        {availableAttributes.length === 0 ? (
          <Menu.Item label={t('noFieldsAvailable')} isDisabled />
        ) : (
          availableAttributes.map((attr) => {
            return (
              <Menu.Item
                key={attr.shortId}
                label={attr.title}
                icon={<AttributeIcon type={attr.attributeType} color="elementPurple50" />}
                onSelect={() => {
                  onSelect(attr.shortId, attr.title, attr.attributeType);
                }}
              />
            );
          })
        )}
      </Menu>
    </Stack>
  );
};

type EmptyJourneyFieldsProps = {
  text: string;
};

const EmptyJourneyFields = ({ text }: EmptyJourneyFieldsProps) => {
  return (
    <EmptyJourneyFieldsContainer>
      <IconAttributeJourney color="elementPurple50" />
      <Text font="regular14" color="textSecondary" text={text} />
    </EmptyJourneyFieldsContainer>
  );
};

const EmptyJourneyFieldsContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: pxToRem(16),
  padding: pxToRem(16),
  borderRadius: pxToRem(8),
  background: theme.color.uiTrans5,
});

const EmailTriggerLogs = ({ triggerShortId }: { triggerShortId: string }) => {
  const { t } = useScopedTranslate(locale);
  const { data, isLoading } = useEmailTriggerLogs(triggerShortId);
  const showPreviewLog = usePreviewEmailTriggerLog();

  if (!data || isLoading) {
    return <Spinner />;
  }

  if (data.length === 0) {
    return (
      <EmptyState
        header={t('noLogs')}
        content={t('noLogsDescription')}
        icon={<IconEmail />}
        variant="small"
      />
    );
  }

  return (
    <Stack space={2}>
      {data.map((log) => {
        const isError = Boolean(log.errorMessage);
        return (
          <TriggerLogListCard
            key={log.shortId}
            icon={<IconCode color={isError ? 'elementRed100' : 'elementPurple80'} />}
            title={abbreviateDate(log.triggeredOnDate, 'en')}
            description={log.triggeredOnTime}
            iconRight={
              <Badge
                size="md"
                icon={isError ? <IconCloseSmall /> : <IconCheckmarkSmall />}
                variant={isError ? 'red' : 'green'}
              />
            }
            onClick={() => {
              showPreviewLog({ triggerShortId, logShortId: log.shortId });
            }}
          />
        );
      })}
    </Stack>
  );
};

type FooterProps = {
  trigger: JourneyEmailTrigger;
  isDisabled: boolean;
};

const Footer = ({ trigger, isDisabled }: FooterProps) => {
  const { t } = useScopedTranslate(locale);
  const deleteMutation = useDeleteEmailTrigger(trigger.journeyId);
  const showDelete = useDeleteModal();

  const handleCancel = async () => {
    if (!trigger.isConfigured) {
      await deleteMutation.mutateAsync(trigger.shortId, { onSuccess: forceHideSidebar });
    } else {
      forceHideSidebar();
    }
  };

  return (
    <Sidebar.Footer>
      <Stack orientation="horizontal" justify="space-between" align="center">
        <IconButton
          variant="ghost-white"
          icon={<IconTrash color="purple50" />}
          onClick={() => {
            showDelete({
              title: t('removeTitle'),
              description: t('removeDescription'),
              buttonText: t('core.delete'),
              hasUserConfirmation: true,
              onConfirm: () => {
                return deleteMutation.mutateAsync(trigger.shortId, {
                  onSuccess: forceHideSidebar,
                });
              },
            });
          }}
        />
        <Stack orientation="horizontal" space={2}>
          <Button variant="secondary" text={t('core.cancel')} onClick={handleCancel} />
          <Form.Submit
            data-cy="action-save-btn"
            variant="primary-blue"
            text={t('core.save')}
            isDisabled={isDisabled}
          />
        </Stack>
      </Stack>
    </Sidebar.Footer>
  );
};

export const locale = {
  en: {
    emailTrigger: 'Mailhook',
    details: 'Details',
    logs: 'Logs',
    triggerDetails: 'Trigger details',
    triggerEmail: 'Trigger email address',
    senderEmail: 'Sender email',
    senderName: 'Sender name',
    emailSubject: 'Subject',
    travellerFields: 'Traveller profile fields',
    journeyFields: 'Journey fields',
    defineDataSubheading: 'Define the data the hook should generate',
    instructionsInfo:
      'Because the emails come as a body of text we need AI to extract the right content from the email. Run Test / Configure email trigger to do this automatically, or manually add instructions for the fields you want to extract.',
    testConfigureEmailTrigger: 'Test / Configure email trigger',
    fullName: 'Full Name',
    email: 'Email',
    addField: 'Add field',
    noInstructions: 'No instructions yet',
    noJourneyFields: 'No fields added',
    noFieldsAvailable: 'No fields available',
    helpText:
      'A Mailhook trigger starts a workflow when an email is received at a unique trigger address. AI extracts structured data from the email body based on per-field instructions you configure. Full Name and Email are always extracted automatically. You can add additional fields and use Test / Configure to generate extraction instructions from a sample email.',
    addConditions: 'Add conditions',
    options: 'Options',
    active: 'Active',
    startOnSubmission: 'Start on submission',
    allowDuplicates: 'Allow duplicate travellers',
    success: 'Success',
    noLogs: 'No logs',
    noLogsDescription: 'This email trigger has never been activated',
    removeTitle: 'Delete email trigger',
    removeDescription: 'Are you sure you want to delete this email trigger?',
  },
};
